import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ClearanceCategoryNode, getClearanceCategoryTree } from "@SupplyLand/Services/SupplyLandService";
import { applyClearanceProductIds } from "@SupplyLand/Store/Pages/ProductList/ClearanceProductFilter";
import {
    buildMaps,
    CategoryMaps,
    computeProductIds,
    deriveSelectedCategoryIds,
    getTopMostSelectedNodes,
    isClearanceCategory,
    removeBranchFromSelection,
} from "@SupplyLand/Utilities/clearanceCategoryTree";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Tag from "@insite/mobius/Tag";
import Typography from "@insite/mobius/Typography";
import React, { FC, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps, HasCategoryContext {}

const mapStateToProps = (state: ApplicationState) => ({
    appliedProductIds: state.pages.productList.parameter?.productIds as string[] | undefined,
    isCategoryPage: state.pages.productList.productFilters.pageCategoryId !== undefined,
});

const mapDispatchToProps = {
    applyClearanceProductIds,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const ClearanceCategorySelectedFilters: FC<Props> = ({
    appliedProductIds,
    isCategoryPage,
    applyClearanceProductIds,
    category,
}) => {
    const [tree, setTree] = useState<ClearanceCategoryNode[]>([]);

    const clearanceCategoryId = category?.id;
    const isClearance = isClearanceCategory(category);

    useEffect(() => {
        let isMounted = true;

        if (isClearance && clearanceCategoryId) {
            getClearanceCategoryTree({ categoryId: clearanceCategoryId })
                .then(response => {
                    if (isMounted && response?.success) {
                        setTree(response.categories || []);
                    }
                })
                .catch(error => logger.error(error));
        } else {
            setTree([]);
        }

        return () => {
            isMounted = false;
        };
    }, [clearanceCategoryId, isClearance]);

    const maps = useMemo<CategoryMaps>(() => buildMaps(tree), [tree]);

    const selectedCategoryIds = useMemo(
        () => deriveSelectedCategoryIds(maps, appliedProductIds),
        [maps, appliedProductIds],
    );

    const selectedNodes = useMemo(
        () => getTopMostSelectedNodes(selectedCategoryIds, maps),
        [selectedCategoryIds, maps],
    );

    if ((isCategoryPage && !isClearance) || !tree.length || !selectedNodes.length) {
        return null;
    }

    const onRemove = (node: ClearanceCategoryNode) => {
        const next = removeBranchFromSelection(node, selectedCategoryIds, maps);
        applyClearanceProductIds(computeProductIds(next, maps), clearanceCategoryId);
    };

    const onClearAll = () => {
        applyClearanceProductIds([], clearanceCategoryId);
    };

    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                margin-bottom: 20px;
            `}
            data-test-selector="clearanceCategorySelectedFilters"
        >
            <Typography
                css={css`
                    margin: 0 10px 10px 0;
                `}
            >
                Category:
            </Typography>
            {selectedNodes.map(node => (
                <Tag
                    key={node.id}
                    color="#E0E0E0"
                    onDelete={() => onRemove(node)}
                    typographyProps={{ weight: "bold", color: "#000000" }}
                    iconProps={{ src: "X", color: "#000000", size: 14 }}
                    css={css`
                        align-items: center;
                        border-radius: 0;
                        min-height: 0;
                        padding: 0 4px 0 10px;
                        button {
                            border-radius: 0;
                        }
                    `}
                    data-test-selector={`clearanceCategorySelectedFilter${node.id}`}
                >
                    {node.shortDescription}
                </Tag>
            ))}
            <Link
                onClick={onClearAll}
                typographyProps={{ weight: "bold" }}
                css={css`
                    display: inline-flex;
                    align-items: center;
                    margin: 0 0 10px 10px;
                `}
                data-test-selector="clearanceCategoryClearAll"
            >
                Clear All
                <Icon
                    src="X"
                    size={14}
                    css={css`
                        margin-left: 8px;
                    `}
                />
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(ClearanceCategorySelectedFilters)),
    definition: {
        group: "Product List",
        displayName: "Remove Clearance Filters",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
